// src/pages/IndicateurDHD.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import DownloadIcon from '@mui/icons-material/Download';
import {
    Container,
    Typography,
    Grid,
    Card,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    styled,
    CardMedia,
} from '@mui/material';
import Footer from '../navbar/Footer';
import moment from 'moment';
import { getPublications } from '../services/publicationService';

const StyledTitle = styled(Typography)(({ theme }) => ({
    color: 'green',
    marginBottom: theme.spacing(1),
}));

const StyledDescription = styled(Typography)(({ theme }) => ({
    textAlign: 'left',
    marginBottom: theme.spacing(2),
    color: '#555',
}));

const ITEMS_PER_PAGE = 2;

function IndicateurDHD() {
    const [filterYear, setFilterYear] = useState('all');
    const [page, setPage] = useState(0);
    const [indicateursData, setIndicateursData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Chargement des données depuis l'API
    useEffect(() => {
        setLoading(true);
        getPublications({ type: 'idhd', perPage: 1000 })
            .then((res) => {
                if (res.error) {
                    setError(res.error);
                } else {
                    // Mapper les données de l'API vers la structure attendue
                    const mapped = res.data.map((item) => ({
                        title: item.title,
                        link: item.document_url,
                        image: item.image_url,
                        description: item.description,
                        publicationDate: new Date(item.publication_date), // utilisé pour le filtre et l'affichage
                    }));
                    // Trier par date de publication décroissante (plus récent en premier)
                    const sorted = mapped.sort((a, b) => b.publicationDate - a.publicationDate);
                    setIndicateursData(sorted);
                }
            })
            .catch((err) => {
                setError('Erreur lors du chargement des données.');
                console.error(err);
            })
            .finally(() => setLoading(false));
    }, []);

    const handleFilterChange = (event) => {
        setFilterYear(event.target.value);
        setPage(0); // réinitialiser la pagination
    };

    const handleLoadMore = () => {
        setPage(prevPage => prevPage + 1);
    };

    // Années disponibles (basées sur publicationDate)
    const years = useMemo(() => {
        const yearsSet = new Set(indicateursData.map(item => item.publicationDate.getFullYear()));
        return [...yearsSet].sort((a, b) => b - a);
    }, [indicateursData]);

    // Filtrage par année
    const filteredData = useMemo(() => {
        if (filterYear === 'all') return indicateursData;
        return indicateursData.filter(item => item.publicationDate.getFullYear() === parseInt(filterYear));
    }, [indicateursData, filterYear]);

    // Pagination
    const displayedData = filteredData.slice(0, (page + 1) * ITEMS_PER_PAGE);
    const hasMore = displayedData.length < filteredData.length;

    if (loading) {
        return (
            <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '20px' }}>
                <NavigationBar />
                <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
                    <StyledTitle variant="h4" align="justify" paragraph>
                        Indicateurs de Développement Humain Durable (DHD)
                    </StyledTitle>
                    <StyledDescription variant="body1" align="justify" paragraph>
                        Chargement des données...
                    </StyledDescription>
                </Container>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '20px' }}>
                <NavigationBar />
                <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
                    <StyledTitle variant="h4" align="justify" paragraph>
                        Indicateurs de Développement Humain Durable (DHD)
                    </StyledTitle>
                    <StyledDescription variant="body1" align="justify" paragraph color="error">
                        {error}
                    </StyledDescription>
                </Container>
                <Footer />
            </div>
        );
    }

    return (
        <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '20px' }}>
            <NavigationBar />
            <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
                <StyledTitle variant="h4" align="justify" paragraph>
                    Indicateurs de Développement Humain Durable (DHD)
                </StyledTitle>

                <StyledDescription variant="body1" align="justify" paragraph>
                    Cette section présente les Indicateurs de Développement Humain Durable (DHD) au Burundi, une base de données essentielle pour les chercheurs et les acteurs du développement. L'objectif principal de cette base est de fournir des indicateurs de développement réguliers et fiables, qui permettront d'élaborer des stratégies et programmes socio-économiques à la fois adaptés, durables et réalisables. Elle regroupe des données chiffrées diversifiées, organisées en neuf modules clés : « Population, habitat et emploi » ; « Enseignement et alphabétisation » ; « Santé et nutrition » ; « Aide extérieure » ; « Environnement » ; « Macroéconomie » ; « Communication » ; « Dépenses militaires » ; et « Pauvreté ».
                </StyledDescription>

                <FormControl fullWidth margin="normal" style={{ marginLeft: '0px', width: '300px' }}>
                    <InputLabel id="year-filter-label">Sélectionnez par l'année de publication</InputLabel>
                    <Select
                        labelId="year-filter-label"
                        id="year-filter"
                        value={filterYear}
                        label="Sélectionnez par l'année de publication"
                        onChange={handleFilterChange}
                    >
                        <MenuItem value="all">Tous les indicateurs (DHD)</MenuItem>
                        {years.map(year => (
                            <MenuItem key={year} value={year}>{year}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Grid container spacing={1} style={{ marginTop: '10px' }}>
                    {displayedData.map((indice, index) => (
                        <Grid item xs={12} sm={12} key={index}>
                            <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex' }}>
                                    <a
                                        href={indice.link}
                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <CardMedia
                                            component="img"
                                            sx={{ width: 151, height: 200, m: 2 }}
                                            image={indice.image}
                                            alt={indice.title}
                                        />
                                    </a>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                                        <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                                            <a
                                                href={indice.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ textDecoration: 'none', color: 'green' }}
                                            >
                                                <DownloadIcon /> {indice.title}
                                            </a>
                                        </Typography>
                                        <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                                            {indice.description}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Publié en {moment(indice.publicationDate).format('MMMM YYYY')}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {hasMore && (
                    <Box textAlign="left" mt={2}>
                        <Button variant="contained" color="success" onClick={handleLoadMore}>
                            Plus de rapports
                        </Button>
                    </Box>
                )}
            </Container>
            <br />
            <br />
            <Footer />
        </div>
    );
}

export default IndicateurDHD;